import { mascara, numero } from './ValorDoMes';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ConviteDeMeta, MetaCompartilhada,
  aportarNaMeta, cancelarConviteDaMeta, convidarParaMeta, criarMetaCompartilhada,
  encerrarMeta, getMetasCompartilhadas, responderConviteDaMeta, sairDaMeta,
} from '../services/api';
import { AcoesDaMeta } from './MetaCompartilhadaCard';
import { useConfirmar } from '../contexts/Confirmacao';
import './MetaCompartilhada.css';

/**
 * O estado das metas compartilhadas, e as ações sobre elas.
 *
 * ── Por que um hook, e não um componente que desenha tudo ────────────────
 *
 * Porque as peças não moram no mesmo lugar da tela. Os CARDS vão dentro da
 * mesma `goals-grid` das reservas pessoais — é o que faz a compartilhada ser
 * IRMÃ da pessoal em vez de uma lista à parte, que era a decisão inteira do
 * desenho. Os convites e o formulário de criar ficam fora da grade.
 *
 * E `Goals.tsx` precisa da CONTAGEM antes de decidir o que desenhar: com
 * nenhuma reserva pessoal e uma meta compartilhada, o estado vazio de reservas
 * ("Nenhuma reserva criada", com o preview fantasma) seria uma mentira em cima
 * de uma tela que tem meta.
 *
 * ── Recarrega tudo depois de cada ação ───────────────────────────────────
 *
 * E não atualiza o estado local. O saldo é DERIVADO no servidor
 * (`SUM(meta_aporte.valor)`), e quem mais está na meta pode ter aportado entre
 * o meu clique e a resposta. Somar localmente criaria um número que só existe
 * na minha tela — a divergência entre escalar e livro que a arquitetura inteira
 * desta feature existe para não ter.
 */
export const useMetasCompartilhadas = (aoMudarDinheiro?: () => void) => {
  const confirmar = useConfirmar();
  const [metas, setMetas] = useState<MetaCompartilhada[]>([]);
  const [convites, setConvites] = useState<ConviteDeMeta[]>([]);
  /*
   * Carregar não esvazia o que já está na tela — a mesma decisão de
   * `Goals.tsx`: trocar a seção por um "carregando" a cada volta fazia a
   * página pular. O vazio só vale antes da primeira resposta.
   */
  const [carregou, setCarregou] = useState(false);

  const recarregar = useCallback(async () => {
    try {
      const r = await getMetasCompartilhadas();
      setMetas(r.metas);
      setConvites(r.convites);
    } catch (e) {
      /*
       * Falhar aqui NÃO limpa a lista: a faixa de erro aparece por cima do que
       * já estava, em vez de a meta de alguém sumir da tela porque a rede
       * piscou. É o estado "mantém a lista velha" do desenho.
       */
      console.error('[meta] não consegui recarregar as metas compartilhadas', e);
    } finally {
      setCarregou(true);
    }
  }, []);

  useEffect(() => { recarregar(); }, [recarregar]);

  /*
   * `aoMudarDinheiro` avisa o App para recarregar transações e projeção.
   *
   * Um aporte é uma DESPESA de verdade em `transactions` (com
   * `debit_direction = 'debit'`), então o caixa do mês muda. Sem este aviso, a
   * barra da meta subiria e o saldo do topo continuaria o de antes — dois
   * números sobre o mesmo dinheiro, discordando na mesma tela.
   */
  const depois = async (mexeuNoDinheiro = false) => {
    await recarregar();
    if (mexeuNoDinheiro) aoMudarDinheiro?.();
  };

  const acoes: AcoesDaMeta = {
    aportar: async (id, valor) => { await aportarNaMeta(id, valor); await depois(true); },
    convidar: async (id, email) => { await convidarParaMeta(id, email); await depois(); },
    cancelarConvite: async (id, membro) => {
      await cancelarConviteDaMeta(id, membro); await depois();
    },
    /*
     * Sair e encerrar perguntam antes (16/09): eram um clique só, num link
     * pequeno ao lado de outro, e nenhum dos dois se desfaz por aqui.
     */
    sair: async id => {
      const nome = metas.find(m => m.id === id)?.nome ?? 'esta meta';
      if (!await confirmar({
        titulo: `Sair da meta "${nome}"?`,
        texto: 'Ela some da sua lista. O que você já guardou continua no total do grupo, '
          + 'e você só volta se alguém te convidar de novo.',
        acao: 'Sair da meta', perigo: true,
      })) return;
      await sairDaMeta(id); await depois();
    },
    encerrar: async id => {
      const nome = metas.find(m => m.id === id)?.nome ?? 'esta meta';
      if (!await confirmar({
        titulo: `Encerrar a meta "${nome}"?`,
        texto: 'Ela sai da lista de todo mundo que participa, e ninguém mais consegue '
          + 'guardar dinheiro nela. O que cada um guardou fica registrado. Não dá para desfazer.',
        acao: 'Encerrar meta', perigo: true,
      })) return;
      await encerrarMeta(id); await depois();
    },
  };

  const responder = async (membroId: string, resposta: 'aceitar' | 'recusar') => {
    await responderConviteDaMeta(membroId, resposta);
    await recarregar();
  };

  const criar = async (nome: string, alvo: number) => {
    await criarMetaCompartilhada({ nome, alvo });
    await recarregar();
  };

  return { metas, convites, carregou, acoes, responder, criar, recarregar };
};

/**
 * Os convites que me esperam.
 *
 * ── Os dois botões têm o mesmo peso ──────────────────────────────────────
 *
 * Um "recusar" pequeno e cinza embaixo é o padrão escuro clássico, e
 * transformaria isto num consentimento fabricado. `ConvitePublico.tsx` já
 * tomou essa posição para o convite do relatório, e ela vale aqui pela mesma
 * razão: se recusar é mais difícil que aceitar, o "sim" não quer dizer nada.
 */
export const ConvitesDeMeta: React.FC<{
  convites: ConviteDeMeta[];
  onResponder: (membroId: string, resposta: 'aceitar' | 'recusar') => Promise<void>;
}> = ({ convites, onResponder }) => {
  /** Convite e resposta (`id:aceitar`): só o botão tocado se enche. */
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  if (convites.length === 0) return null;

  const responder = async (id: string, r: 'aceitar' | 'recusar') => {
    setOcupado(`${id}:${r}`);
    setErro(null);
    try {
      await onResponder(id, r);
    } catch (e: any) {
      setErro(e?.response?.data?.error ?? 'Não consegui registrar sua resposta.');
    } finally {
      setOcupado(null);
    }
  };

  return (
    <section className="meta-convites-recebidos">
      {convites.map(c => (
        <div className="meta-convite-card" key={c.id}>
          <p className="meta-convite-texto">
            <strong>{c.de}</strong> te chamou para juntar dinheiro em{' '}
            <strong>{c.meta}</strong>.
          </p>
          <div className="meta-convite-acoes">
            <button
              type="button" className="meta-btn"
              disabled={ocupado !== null}
              aria-busy={ocupado === `${c.id}:aceitar`}
              onClick={() => responder(c.id, 'aceitar')}
            >
              Entrar
            </button>
            <button
              type="button" className="meta-btn meta-btn-recusar"
              disabled={ocupado !== null}
              aria-busy={ocupado === `${c.id}:recusar`}
              onClick={() => responder(c.id, 'recusar')}
            >
              Agora não
            </button>
          </div>
        </div>
      ))}
      {erro && <p className="meta-erro" role="alert">{erro}</p>}
    </section>
  );
};

/**
 * Criar uma meta compartilhada.
 *
 * ── Por que não é o mesmo formulário das metas pessoais ──────────────────
 *
 * Porque criar uma meta compartilhada tem uma consequência que criar uma
 * reserva não tem: ela existe para outras pessoas entrarem. Enfiar a opção
 * como um terceiro botão de tipo no formulário de metas faria a escolha passar
 * despercebida — e a pessoa descobriria a diferença só quando fosse convidar
 * alguém e não encontrasse onde.
 *
 * Um campo, um alvo, e o convite vem DEPOIS, pelo card. A meta existe antes de
 * ter gente: quem cria já está dentro, e chamar é a segunda decisão.
 */
export const CriarMetaCompartilhada: React.FC<{
  onCriar: (nome: string, alvo: number) => Promise<void>;
  /**
   * O que mostrar ABAIXO do formulário aberto — o exemplo de como a meta fica,
   * para quem ainda não tem nenhuma. Vem de fora porque este arquivo não pode
   * importar o card (o card é que importa tipos daqui a caminho contrário).
   */
  exemplo?: React.ReactNode;
}> = ({ onCriar, exemplo }) => {
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [alvo, setAlvo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const criar = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = numero(alvo);
    if (!nome.trim()) return setErro('Dá um nome para a meta.');
    if (!Number.isFinite(v) || v <= 0) return setErro('O alvo precisa ser maior que zero.');
    setOcupado(true);
    setErro(null);
    try {
      await onCriar(nome.trim(), v);
      /* Só limpa e fecha DEPOIS de dar certo: com erro, o que ela escreveu
         continua no campo, ao lado do motivo. */
      setNome(''); setAlvo(''); setAberto(false);
    } catch (e: any) {
      setErro(e?.response?.data?.error ?? 'Não consegui criar a meta.');
    } finally {
      setOcupado(false);
    }
  };

  if (!aberto) {
    return (
      <button type="button" className="meta-criar-link" onClick={() => setAberto(true)}>
        + Juntar dinheiro com alguém
      </button>
    );
  }

  return (
    <>
    <form className="meta-criar" onSubmit={criar}>
      <input
        value={nome} onChange={e => setNome(e.target.value)}
        placeholder="Viagem Japão 2028" aria-label="Nome da meta compartilhada"
        disabled={ocupado} autoFocus
      />
      <input
        type="text" inputMode="numeric" value={alvo}
        onChange={e => setAlvo(mascara(e.target.value))}
        placeholder="Quanto juntar" aria-label="Alvo da meta compartilhada"
        disabled={ocupado}
      />
      <button className="meta-btn" disabled={ocupado} aria-busy={ocupado}>Criar</button>
      <button
        type="button" className="meta-btn meta-btn-recusar"
        disabled={ocupado} onClick={() => { setAberto(false); setErro(null); }}
      >
        Cancelar
      </button>
      {erro && <p className="meta-erro" role="alert">{erro}</p>}
    </form>
    {exemplo}
    </>
  );
};
